import AppleMonoIcon from '@linktr.ee/arbor/app-icons/AppleMonoIcon';
import GoogleIcon from '@linktr.ee/arbor/app-icons/GoogleIcon';
import { EnvelopeSimpleIcon, PhoneIcon } from '@linktr.ee/arbor/icons';
import React, { useId } from 'react';
import Button from './Button';

interface SocialButtonProps {
  provider: 'google' | 'apple' | 'email' | 'sms';
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
  variant?: 'outline' | 'primary';
  newDesign?: boolean;
}

const NewAppleIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M19.6218 8.81893C19.5058 8.90894 17.4576 10.0631 17.4576 12.6293C17.4576 15.5976 20.0638 16.6477 20.1418 16.6737C20.1298 16.7377 19.7278 18.1119 18.7677 19.512C17.9116 20.7441 17.0175 21.9742 15.6574 21.9742C14.2973 21.9742 13.9472 21.1842 12.3771 21.1842C10.8469 21.1842 10.3029 22.0002 9.05874 22.0002C7.81461 22.0002 6.94653 20.8601 5.94843 19.46C4.79231 17.8158 3.85822 15.2616 3.85822 12.8373C3.85822 8.94894 6.38647 6.88673 8.87472 6.88673C10.1969 6.88673 11.299 7.75482 12.129 7.75482C12.9191 7.75482 14.1512 6.83473 15.6554 6.83473C16.2255 6.83473 18.2737 6.88673 19.6218 8.81893ZM14.9413 5.18856C15.5634 4.45049 16.0034 3.42639 16.0034 2.40229C16.0034 2.26027 15.9914 2.11626 15.9654 2.00025C14.9533 2.03825 13.7492 2.67431 13.0231 3.5164C12.4531 4.16446 11.921 5.18856 11.921 6.22667C11.921 6.38268 11.947 6.5387 11.959 6.5887C12.023 6.6007 12.127 6.61471 12.2311 6.61471C13.1391 6.61471 14.2813 6.00665 14.9413 5.18856Z"
      fill="currentColor"
    />
  </svg>
);

const NewGoogleIcon = () => {
  const id = useId().replace(/:/g, '');

  return (
    <svg
      aria-hidden="true"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id={`${id}-mask0_1_16`}
        style={{ maskType: 'luminance' }}
        maskUnits="userSpaceOnUse"
        x="3"
        y="3"
        width="18"
        height="18"
      >
        <path
          d="M20.6432 10.3303H12.1884V13.7905H17.0466C16.9685 14.2802 16.7931 14.7619 16.5363 15.2012C16.2421 15.7045 15.8784 16.0876 15.5055 16.3794C14.3888 17.2535 13.0868 17.4322 12.1825 17.4322C9.89834 17.4322 7.94664 15.9243 7.1911 13.8754C7.16061 13.8011 7.14036 13.7243 7.11571 13.6484C6.94875 13.1269 6.85752 12.5746 6.85752 12.0006C6.85752 11.4032 6.95631 10.8313 7.13643 10.2912C7.84689 8.16106 9.84259 6.57005 12.1842 6.57005C12.6552 6.57005 13.1087 6.62731 13.5388 6.74152C14.5218 7.00253 15.2171 7.51659 15.6431 7.92321L18.2139 5.35182C16.6501 3.88738 14.6116 3 12.1799 3C10.2359 2.99996 8.44117 3.61859 6.97043 4.66409C5.7777 5.51196 4.79949 6.64716 4.13932 7.96557C3.52527 9.188 3.18904 10.5427 3.18904 11.9992C3.18904 13.4558 3.52578 14.8246 4.13984 16.0357V16.0439C4.78843 17.3297 5.7369 18.4367 6.88966 19.2807C7.89672 20.018 9.70247 21 12.1799 21C13.6046 21 14.8673 20.7376 15.9809 20.246C16.7842 19.8913 17.4959 19.4287 18.1403 18.8341C18.9918 18.0485 19.6586 17.0768 20.1138 15.9589C20.569 14.8409 20.8124 13.5766 20.8124 12.206C20.8124 11.5677 20.7496 10.9194 20.6432 10.3302V10.3303Z"
          fill="white"
        />
      </mask>
      <g mask={`url(#${id}-mask0_1_16)`}>
        <g filter={`url(#${id}-filter0_f_1_16)`}>
          <path
            d="M3.05861 12.0604C3.06795 13.494 3.46791 14.9731 4.07332 16.1672V16.1754C4.51075 17.0426 5.10859 17.7276 5.78953 18.4063L9.9022 16.8736C9.1241 16.4699 9.00537 16.2225 8.44761 15.7711C7.87762 15.1841 7.45281 14.5102 7.18825 13.72H7.17759L7.18825 13.7118C7.0142 13.1899 6.99703 12.636 6.99061 12.0604H3.05861Z"
            fill={`url(#${id}-paint0_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter1_f_1_16)`}>
          <path
            d="M12.1881 2.93459C11.7816 4.39318 11.9371 5.81098 12.1881 6.63592C12.6575 6.63628 13.1097 6.69343 13.5385 6.80728C14.5215 7.0683 15.2167 7.58237 15.6428 7.98899L18.2793 5.35191C16.7174 3.88921 14.8377 2.93689 12.1881 2.93459Z"
            fill={`url(#${id}-paint1_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter2_f_1_16)`}>
          <path
            d="M12.179 2.92305C10.1852 2.92301 8.34434 3.55752 6.83584 4.62986C6.27573 5.02803 5.76173 5.48797 5.30405 5.99944C5.18415 7.14833 6.20161 8.56044 8.21649 8.54876C9.1941 7.38727 10.64 6.63579 12.2492 6.63579C12.2507 6.63579 12.2521 6.63592 12.2536 6.63592L12.1879 2.92331C12.1849 2.92331 12.182 2.92305 12.179 2.92305Z"
            fill={`url(#${id}-paint2_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter3_f_1_16)`}>
          <path
            d="M18.76 12.4761L16.9803 13.7248C16.9022 14.2145 16.7268 14.6963 16.47 15.1355C16.1758 15.6388 15.812 16.022 15.4392 16.3138C14.3248 17.186 13.0262 17.3656 12.1222 17.3664C11.1878 18.9918 11.024 19.8059 12.1879 21.1177C13.6281 21.1167 14.9049 20.8511 16.0311 20.3539C16.8452 19.9944 17.5665 19.5256 18.2195 18.9231C19.0824 18.127 19.7583 17.1422 20.2196 16.0092C20.6809 14.8762 20.9275 13.5951 20.9275 12.2061L18.76 12.4761Z"
            fill={`url(#${id}-paint3_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter4_f_1_16)`}>
          <path
            d="M12.056 10.1989V13.922H20.6184C20.6937 13.4121 20.9428 12.7523 20.9428 12.2061C20.9428 11.5677 20.88 10.788 20.7736 10.1989H12.056Z"
            fill="#3086FF"
          />
        </g>
        <g filter={`url(#${id}-filter5_f_1_16)`}>
          <path
            d="M5.34482 5.86805C4.81643 6.45853 4.36502 7.11945 4.00711 7.83423C3.39307 9.05666 3.05685 10.5428 3.05685 11.9993C3.05685 12.0199 3.05851 12.0399 3.05865 12.0604C3.33059 12.593 6.81502 12.491 6.99065 12.0604C6.99043 12.0403 6.98821 12.0208 6.98821 12.0006C6.98821 11.4032 7.08703 10.9629 7.26715 10.4228C7.48934 9.75658 7.83726 9.14309 8.28214 8.61453C8.38299 8.48302 8.652 8.20031 8.73048 8.03074C8.76038 7.96615 8.6762 7.92989 8.67149 7.90716C8.66623 7.88173 8.55338 7.90218 8.52809 7.88324C8.44782 7.82309 8.28885 7.79169 8.19232 7.76377C7.986 7.7041 7.64407 7.57252 7.45415 7.43612C6.85382 7.00496 5.91695 6.48994 5.34482 5.86805Z"
            fill={`url(#${id}-paint4_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter6_f_1_16)`}>
          <path
            d="M7.46669 7.90968C8.85879 8.77097 9.25914 7.47494 10.1847 7.06938L8.57466 3.65924C7.9824 3.91349 7.42283 4.22937 6.90364 4.59844C6.12827 5.14963 5.44356 5.82223 4.87794 6.58767L7.46669 7.90968Z"
            fill={`url(#${id}-paint5_radial_1_16)`}
          />
        </g>
        <g filter={`url(#${id}-filter7_f_1_16)`}>
          <path
            d="M8.03389 16.6101C6.16517 17.2992 5.87262 17.3239 5.7006 18.5067C6.02931 18.8344 6.38249 19.1374 6.7578 19.4122C7.76486 20.1495 9.70201 21.1315 12.1794 21.1315C12.1824 21.1315 12.1851 21.1312 12.188 21.1312V17.3006C12.1862 17.3006 12.184 17.3008 12.1821 17.3008C11.2544 17.3008 10.5131 17.0519 9.75299 16.6191C9.56558 16.5124 9.22557 16.7989 9.05273 16.6708C8.81435 16.4942 8.24066 16.8231 8.03389 16.6101Z"
            fill={`url(#${id}-paint6_radial_1_16)`}
          />
        </g>
      </g>
      <defs>
        <filter
          id={`${id}-filter0_f_1_16`}
          x="3.02771"
          y="12.0295"
          width="6.90538"
          height="6.40767"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter1_f_1_16`}
          x="11.9073"
          y="2.90369"
          width="6.40291"
          height="5.11619"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter2_f_1_16`}
          x="5.26365"
          y="2.89216"
          width="7.02082"
          height="5.68757"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter3_f_1_16`}
          x="11.3369"
          y="12.1752"
          width="9.6215"
          height="8.97347"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter4_f_1_16`}
          x="12.0251"
          y="10.168"
          width="8.94858"
          height="3.78493"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter5_f_1_16`}
          x="3.02596"
          y="5.83715"
          width="5.74183"
          height="6.61636"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter6_f_1_16`}
          x="4.66074"
          y="3.44204"
          width="5.74115"
          height="4.97431"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.1086"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <filter
          id={`${id}-filter7_f_1_16`}
          x="5.66971"
          y="16.5639"
          width="6.54923"
          height="4.59851"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="0.0154472"
            result="effect1_foregroundBlur_1_16"
          />
        </filter>
        <radialGradient
          id={`${id}-paint0_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(9.81987 18.2712) rotate(-92.3395) scale(8.97141 13.1779)"
        >
          <stop offset="0.141612" stopColor="#1ABD4D" />
          <stop offset="0.247515" stopColor="#6EC30D" />
          <stop offset="0.311547" stopColor="#8AC502" />
          <stop offset="0.366013" stopColor="#A2C600" />
          <stop offset="0.445673" stopColor="#C8C903" />
          <stop offset="0.540305" stopColor="#EBCB03" />
          <stop offset="0.615636" stopColor="#F7CD07" />
          <stop offset="0.699345" stopColor="#FDCD04" />
          <stop offset="0.771242" stopColor="#FDCE05" />
          <stop offset="0.860566" stopColor="#FFCE0A" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint1_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(6.21934 -1.52668e-05 -8.74154e-06 8.03195 18.0327 7.79819)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.408458" stopColor="#FB4E5A" />
          <stop offset="1" stopColor="#FF4540" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint2_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(14.636 1.7604) rotate(151.02) scale(9.961 13.517)"
        >
          <stop offset="0.231273" stopColor="#FF4541" />
          <stop offset="0.311547" stopColor="#FF4540" />
          <stop offset="0.457516" stopColor="#FF4640" />
          <stop offset="0.540305" stopColor="#FF473F" />
          <stop offset="0.699346" stopColor="#FF5138" />
          <stop offset="0.771242" stopColor="#FF5B33" />
          <stop offset="0.860566" stopColor="#FF6C29" />
          <stop offset="1" stopColor="#FF8C18" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint3_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(12.3175 19.9528) rotate(-127.454) scale(25.9858 9.59199)"
        >
          <stop offset="0.131546" stopColor="#0CBA65" />
          <stop offset="0.209784" stopColor="#0BB86D" />
          <stop offset="0.297297" stopColor="#09B479" />
          <stop offset="0.396257" stopColor="#08AD93" />
          <stop offset="0.477124" stopColor="#0AA6A9" />
          <stop offset="0.568425" stopColor="#0D9CC6" />
          <stop offset="0.667385" stopColor="#1893DD" />
          <stop offset="0.768727" stopColor="#258BF1" />
          <stop offset="0.858506" stopColor="#3086FF" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint4_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(11.4155 4.62314) rotate(96.6178) scale(9.70378 13.4172)"
        >
          <stop offset="0.366013" stopColor="#FF4E3A" />
          <stop offset="0.457516" stopColor="#FF8A1B" />
          <stop offset="0.540305" stopColor="#FFA312" />
          <stop offset="0.615636" stopColor="#FFB60C" />
          <stop offset="0.771242" stopColor="#FFCD0A" />
          <stop offset="0.860566" stopColor="#FECF0A" />
          <stop offset="0.915033" stopColor="#FECF08" />
          <stop offset="1" stopColor="#FDCD01" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint5_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(9.84255 4.52314) rotate(132.119) scale(4.81979 13.5944)"
        >
          <stop offset="0.315904" stopColor="#FF4C3C" />
          <stop offset="0.603818" stopColor="#FF692C" />
          <stop offset="0.726837" stopColor="#FF7825" />
          <stop offset="0.884534" stopColor="#FF8D1B" />
          <stop offset="1" stopColor="#FF9F13" />
        </radialGradient>
        <radialGradient
          id={`${id}-paint6_radial_1_16`}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(14.6362 22.2394) rotate(-151.02) scale(9.961 13.517)"
        >
          <stop offset="0.231273" stopColor="#0FBC5F" />
          <stop offset="0.311547" stopColor="#0FBC5F" />
          <stop offset="0.366013" stopColor="#0FBC5E" />
          <stop offset="0.457516" stopColor="#0FBC5D" />
          <stop offset="0.540305" stopColor="#12BC58" />
          <stop offset="0.699346" stopColor="#28BF3C" />
          <stop offset="0.771242" stopColor="#38C02B" />
          <stop offset="0.860566" stopColor="#52C218" />
          <stop offset="0.915033" stopColor="#67C30F" />
          <stop offset="1" stopColor="#86C504" />
        </radialGradient>
      </defs>
    </svg>
  );
};

const SocialButton: React.FC<SocialButtonProps> = ({
  provider,
  onClick,
  children,
  className = '',
  loading = false,
  fullWidth = true,
  iconOnly = false,
  variant = 'outline',
  newDesign = false,
}) => {
  const getProviderIcon = () => {
    switch (provider) {
      case 'google':
        return newDesign ? <NewGoogleIcon /> : <GoogleIcon />;
      case 'apple':
        return newDesign ? (
          <NewAppleIcon />
        ) : (
          <AppleMonoIcon className="-translate-y-px" />
        );
      case 'email':
        return (
          <EnvelopeSimpleIcon
            weight={variant === 'primary' ? 'fill' : 'regular'}
          />
        );
      case 'sms':
        return (
          <PhoneIcon weight={variant === 'primary' ? 'fill' : 'regular'} />
        );
      default:
        return null;
    }
  };

  const providerIcon = getProviderIcon();

  return (
    <Button
      onClick={onClick}
      label={iconOnly ? <span className="sr-only">{children}</span> : children}
      icon={providerIcon || undefined}
      iconOnly={iconOnly}
      newDesign={newDesign}
      state={loading ? 'loading' : 'default'}
      variant={variant}
      className={className}
      fullWidth={fullWidth}
    />
  );
};

export default SocialButton;
